import CheckIcon from '@mui/icons-material/Check';
import { ListItemButton, Stack, Typography, alpha } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  title: string;
  selected: boolean;
  subtitle?: string;
  icon?: React.ReactNode;
  /** Farbe des Symbolfeldes — das Produkt trägt im Assistenten seine eigene Farbe. */
  iconColor?: string;
  disabled?: boolean;
  /** Schmale Kachel ohne Symbol, für Listen wie die Regionen. */
  variant?: 'row' | 'tile';

  onSelect(): void;
}

/**
 * Eine wählbare Karte: Symbol, Name, eine Zeile Erklärung, Haken.
 *
 * Vorher waren dieselben Entscheidungen mal ein Radioknopf mit Textzeile, mal eine
 * Listenzeile mit Chip. Wer Produkt, Region und Vertrag hintereinander wählte, sah drei
 * verschiedene Muster für dieselbe Handlung.
 */
const OptionCard: React.FC<Props> = ({ title, selected, subtitle, icon, iconColor, disabled, variant = 'row', onSelect }) => {
  const { classes, cx } = useStyles({ iconColor });

  return (
    <ListItemButton
      className={cx(classes.card, variant, selected ? 'selected' : null)}
      selected={selected}
      disabled={disabled}
      disableRipple={true}
      onClick={onSelect}
    >
      {!_.isNil(icon) ? <div className={classes.iconBox}>{icon}</div> : null}
      <Stack className={classes.texts} gap={0.25}>
        <Typography className={cx(classes.title, variant, selected ? 'selected' : null)}>{title}</Typography>
        {!_.isNil(subtitle) ? <Typography className={classes.subtitle}>{subtitle}</Typography> : null}
      </Stack>
      {variant === 'row' ? (
        <div className={cx(classes.check, selected ? 'selected' : null)}>{selected ? <CheckIcon className={classes.checkIcon} /> : null}</div>
      ) : null}
    </ListItemButton>
  );
};

const useStyles = makeStyles<{ iconColor?: string }>()((theme, { iconColor }) => ({
  card: {
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '12px',
    background: '#FFF',
    transition: '225ms',
    '&.row': {
      padding: '15px 16px',
      gap: theme.spacing(1.75),
    },
    '&.tile': {
      padding: '11px 10px',
      justifyContent: 'center',
      textAlign: 'center',
    },
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.04),
      borderColor: theme.palette.primary.light,
    },
    '&.selected, &.Mui-selected, &.Mui-selected:hover': {
      border: `2px solid ${theme.palette.primary.main}`,
      background: alpha(theme.palette.primary.main, 0.05),
    },
    '&.row.selected': {
      padding: '14px 15px',
    },
    '&.tile.selected': {
      padding: '10px 9px',
    },
  },
  iconBox: {
    width: '40px',
    height: '40px',
    flex: '0 0 auto',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: alpha(iconColor || theme.palette.primary.main, 0.12),
    '& svg': {
      fontSize: theme.typography.pxToRem(21),
      color: iconColor || theme.palette.primary.main,
    },
  },
  texts: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    lineHeight: 1.3,
    '&.tile': {
      fontSize: theme.typography.pxToRem(13.5),
      fontWeight: 'normal',
    },
    '&.tile.selected': {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.4,
    color: theme.palette.greys.medium,
  },
  check: {
    width: '22px',
    height: '22px',
    flex: '0 0 auto',
    borderRadius: '50%',
    border: `2px solid ${theme.palette.greys.light}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&.selected': {
      border: 'none',
      background: theme.palette.primary.main,
    },
  },
  checkIcon: {
    fontSize: theme.typography.pxToRem(14),
    color: '#FFF',
  },
}));

export default OptionCard;
